// ----------------------------------------------------------------------------
// dither.js — Dithering algorithms ported from Dithering Studio
// (https://github.com/Oslonline/dithering-studio) Apache-2.0
//
// All functions follow the glitchfx signature: (canvas, ctx, ...knobArgs) => Promise
// Knob args are passed positionally from the fx.js descriptor inputs.
//
// Common knob layout for error-diffusion FX:
//   (threshold, serpentine, invert, strength)
// Common knob layout for ordered/other FX:
//   (threshold, invert)
// ----------------------------------------------------------------------------

const createCanvas = (w, h) => {
    if (typeof OffscreenCanvas !== 'undefined') {
        return new OffscreenCanvas(w, h);
    }
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
};

// ---- PRNG (mulberry32) ----
function mulberry32(seed) {
    let a = seed >>> 0;
    return function () {
        a |= 0; a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// ---- Blue noise mask (64x64, built once) ----
const BLUE_NOISE_SIZE = 64;
const _BN_TOTAL = BLUE_NOISE_SIZE * BLUE_NOISE_SIZE;
let _blueNoiseMask = null;
function buildBlueNoiseMask() {
    if (_blueNoiseMask) return _blueNoiseMask;
    const rng = mulberry32(0xB1);
    const coordsX = new Uint16Array(_BN_TOTAL);
    const coordsY = new Uint16Array(_BN_TOTAL);
    for (let i = 0; i < _BN_TOTAL; i++) { coordsX[i] = i % BLUE_NOISE_SIZE; coordsY[i] = (i / BLUE_NOISE_SIZE) | 0; }
    const order = [];
    const chosen = new Uint8Array(_BN_TOTAL);
    const first = (rng() * _BN_TOTAL) | 0;
    chosen[first] = 1; order.push(first);
    const tmpMinDist = new Float32Array(_BN_TOTAL); tmpMinDist.fill(1e9);
    const size = BLUE_NOISE_SIZE;
    function updateDistances(pIdx) {
        const px = coordsX[pIdx], py = coordsY[pIdx];
        for (let i = 0; i < _BN_TOTAL; i++) {
            if (chosen[i]) continue;
            const dx = px - coordsX[i], dy = py - coordsY[i];
            const wx = dx > size / 2 ? dx - size : (dx < -size / 2 ? dx + size : dx);
            const wy = dy > size / 2 ? dy - size : (dy < -size / 2 ? dy + size : dy);
            const d2 = wx * wx + wy * wy; if (d2 < tmpMinDist[i]) tmpMinDist[i] = d2;
        }
    }
    updateDistances(first);
    for (let k = 1; k < _BN_TOTAL; k++) {
        let best = -1, bestScore = -1;
        for (let i = 0; i < _BN_TOTAL; i++) { if (chosen[i]) continue; if (tmpMinDist[i] > bestScore) { bestScore = tmpMinDist[i]; best = i; } }
        chosen[best] = 1; order.push(best); updateDistances(best);
    }
    const ranking = new Uint16Array(_BN_TOTAL);
    for (let i = 0; i < order.length; i++) ranking[order[i]] = i;
    _blueNoiseMask = ranking;
    return ranking;
}
function blueNoiseThreshold(x, y) {
    const mask = buildBlueNoiseMask();
    const v = mask[(y % BLUE_NOISE_SIZE) * BLUE_NOISE_SIZE + (x % BLUE_NOISE_SIZE)];
    return v / (_BN_TOTAL - 1);
}

// ---- Bayer matrices ----
const BAYER_2 = [0, 2, 3, 1];
const BAYER_4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const BAYER_8 = [
    0, 32, 8, 40, 2, 34, 10, 42,
    48, 16, 56, 24, 50, 18, 58, 26,
    12, 44, 4, 36, 14, 46, 6, 38,
    60, 28, 52, 20, 62, 30, 54, 22,
    3, 35, 11, 43, 1, 33, 9, 41,
    51, 19, 59, 27, 49, 17, 57, 25,
    15, 47, 7, 39, 13, 45, 5, 37,
    63, 31, 55, 23, 61, 29, 53, 21
];
function buildBayer(n) {
    if (n === 2) return BAYER_2;
    if (n === 4) return BAYER_4;
    if (n === 8) return BAYER_8;
    let m = [0]; let size = 1;
    while (size < n) {
        const newSize = size * 2;
        const next = new Array(newSize * newSize);
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                const v = m[y * size + x];
                next[y * newSize + x] = 4 * v;
                next[y * newSize + (x + size)] = 4 * v + 2;
                next[(y + size) * newSize + x] = 4 * v + 3;
                next[(y + size) * newSize + (x + size)] = 4 * v + 1;
            }
        }
        m = next; size = newSize;
    }
    return m;
}

// ---- Hilbert curve (for Riemersma) ----
function hilbertCurve(order) {
    if (order === 0) return [[0, 0]];
    const previous = hilbertCurve(order - 1);
    const size = 1 << (order - 1);
    const result = [];
    for (const [x, y] of previous) result.push([y, x]);
    for (const [x, y] of previous) result.push([x, y + size]);
    for (const [x, y] of previous) result.push([x + size, y + size]);
    for (const [x, y] of previous) result.push([2 * size - 1 - y, size - 1 - x]);
    return result;
}
function scaleHilbertCurve(curve, width, height) {
    const curveSize = Math.sqrt(curve.length);
    const scaleX = width / curveSize, scaleY = height / curveSize;
    return curve.map(([x, y]) => [Math.floor(x * scaleX), Math.floor(y * scaleY)]);
}

// ---- Helper: apply invert to output ----
function applyInvert(out) {
    for (let i = 0; i < out.length; i += 4) {
        out[i] = 255 - out[i]; out[i + 1] = 255 - out[i + 1]; out[i + 2] = 255 - out[i + 2];
    }
}

// ---- Helper: compute mean map (integral image) for adaptive FS ----
function computeMeanMap(src, width, height, radius) {
    const lum = new Float32Array(width * height);
    for (let i = 0, p = 0; i < src.length; i += 4, p++) lum[p] = 0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2];
    const stride = width + 1; const integral = new Float32Array(stride * (height + 1));
    for (let y = 0; y < height; y++) {
        let rowSum = 0;
        for (let x = 0; x < width; x++) { const idx = y * width + x; rowSum += lum[idx]; integral[(y + 1) * stride + (x + 1)] = integral[y * stride + (x + 1)] + rowSum; }
    }
    const out = new Float32Array(width * height); const r = radius;
    for (let y = 0; y < height; y++) {
        const y0 = Math.max(0, y - r); const y1 = Math.min(height - 1, y + r);
        for (let x = 0; x < width; x++) {
            const x0 = Math.max(0, x - r); const x1 = Math.min(width - 1, x + r);
            const A = integral[y0 * stride + x0]; const B = integral[y0 * stride + (x1 + 1)];
            const C = integral[(y1 + 1) * stride + x0]; const D = integral[(y1 + 1) * stride + (x1 + 1)];
            const area = (x1 - x0 + 1) * (y1 - y0 + 1);
            out[y * width + x] = (D - B - C + A) / area;
        }
    }
    return out;
}

// ============================================================================
//  CORE RUNNER — reads canvas, runs algorithm on pixel data, writes back
// ============================================================================
// runner signature: (srcData, width, height, params) => Uint8ClampedArray
// params = { threshold, serpentine, invert, strength }
function makeFxRunner(runner) {
    return (canvas, ctx, ...args) => {
        return new Promise((resolve, reject) => {
            try {
                const W = canvas.width, H = canvas.height;
                const imageData = ctx.getImageData(0, 0, W, H);
                const params = {
                    threshold: args[0] !== undefined ? args[0] : 128,
                    serpentine: !!args[1],
                    invert: !!args[2],
                    strength: args[3] !== undefined ? args[3] : 100,
                };
                const result = runner(imageData.data, W, H, params);
                const outData = result instanceof Uint8ClampedArray ? result : result.data;
                const newImageData = new ImageData(new Uint8ClampedArray(outData), W, H);
                ctx.putImageData(newImageData, 0, 0);
                resolve();
            } catch (e) {
                reject(e);
            }
        });
    };
}

// ============================================================================
//  ERROR DIFFUSION — generic kernel runner
// ============================================================================
function createKernelRunner(matrix, divisor) {
    return function (srcData, width, height, params) {
        const out = new Uint8ClampedArray(srcData);
        const userT = params.threshold ?? 128;
        const useSerp = params.serpentine;
        const strengthMultiplier = (params.strength ?? 100) / 100;
        for (let y = 0; y < height; y++) {
            const leftToRight = !useSerp || y % 2 === 0;
            for (let xi = leftToRight ? 0 : width - 1; leftToRight ? xi < width : xi >= 0; leftToRight ? xi++ : xi--) {
                const x = xi;
                const idx = (y * width + x) * 4;
                const oldLum = out[idx];
                const newLum = oldLum < userT ? 0 : 255;
                const err = (oldLum - newLum) * strengthMultiplier;
                out[idx] = newLum; out[idx + 1] = newLum; out[idx + 2] = newLum;
                for (let j = 0; j < matrix.length; j++) {
                    for (let i = 0; i < matrix[j].length; i++) {
                        const w = matrix[j][i]; if (!w) continue;
                        const dx = i - Math.floor(matrix[j].length / 2);
                        const dy = j;
                        const nx = x + (leftToRight ? dx : -dx);
                        const ny = y + dy;
                        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                            const nIdx = (ny * width + nx) * 4;
                            const diff = (err * w) / divisor;
                            out[nIdx] += diff; out[nIdx + 1] += diff; out[nIdx + 2] += diff;
                        }
                    }
                }
            }
        }
        if (params.invert) applyInvert(out);
        return out;
    };
}

// ---- Floyd–Steinberg (optimised) ----
function runFloydSteinberg(srcData, width, height, params) {
    const { threshold = 128, invert, serpentine, strength = 100 } = params;
    const out = new Uint8ClampedArray(srcData);
    const sm = strength / 100;
    const FS_7 = (7 / 16) * sm, FS_3 = (3 / 16) * sm, FS_5 = (5 / 16) * sm, FS_1 = (1 / 16) * sm;
    const lum = new Float32Array(width * height);
    for (let i = 0, p = 0; i < srcData.length; i += 4, p++) lum[p] = 0.299 * srcData[i] + 0.587 * srcData[i + 1] + 0.114 * srcData[i + 2];
    for (let y = 0; y < height; y++) {
        const serp = serpentine && (y & 1) === 1;
        if (!serp) {
            for (let x = 0; x < width; x++) {
                const idx = y * width + x;
                const oldVal = lum[idx];
                const newVal = oldVal < threshold ? 0 : 255;
                const err = oldVal - newVal;
                lum[idx] = newVal;
                if (x + 1 < width) lum[idx + 1] += err * FS_7;
                if (y + 1 < height) {
                    const nr = idx + width;
                    if (x > 0) lum[nr - 1] += err * FS_3;
                    lum[nr] += err * FS_5;
                    if (x + 1 < width) lum[nr + 1] += err * FS_1;
                }
            }
        } else {
            for (let x = width - 1; x >= 0; x--) {
                const idx = y * width + x;
                const oldVal = lum[idx];
                const newVal = oldVal < threshold ? 0 : 255;
                const err = oldVal - newVal;
                lum[idx] = newVal;
                if (x - 1 >= 0) lum[idx - 1] += err * FS_7;
                if (y + 1 < height) {
                    const nr = idx + width;
                    if (x + 1 < width) lum[nr + 1] += err * FS_3;
                    lum[nr] += err * FS_5;
                    if (x - 1 >= 0) lum[nr - 1] += err * FS_1;
                }
            }
        }
    }
    for (let p = 0, i = 0; p < lum.length; p++, i += 4) {
        let v = lum[p]; if (v < 0) v = 0; else if (v > 255) v = 255;
        if (invert) v = 255 - v;
        out[i] = out[i + 1] = out[i + 2] = v; out[i + 3] = 255;
    }
    return out;
}

// ---- Atkinson ----
function runAtkinson(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const thresh = params.threshold ?? 128;
    const sm = (params.strength ?? 100) / 100;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const pxIndex = (y * width + x) * 4;
            const origLum = out[pxIndex];
            const newLum = origLum < thresh ? 0 : 255;
            const error = ((origLum - newLum) / 8) * sm;
            out[pxIndex] = newLum; out[pxIndex + 1] = newLum; out[pxIndex + 2] = newLum;
            const coords = [[x + 1, y], [x + 2, y], [x - 1, y + 1], [x, y + 1], [x + 1, y + 1], [x, y + 2]];
            for (const [nx, ny] of coords) {
                if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                    const nIdx = (ny * width + nx) * 4;
                    out[nIdx] += error; out[nIdx + 1] += error; out[nIdx + 2] += error;
                }
            }
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

// ---- False Floyd–Steinberg ----
function runFalseFloydSteinberg(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const serp = !!params.serpentine;
    const thresh = params.threshold ?? 128;
    const sm = (params.strength ?? 100) / 100;
    function distribute(x, y, err, l2r) {
        const e3 = (err * 3) / 8 * sm, e2 = (err * 2) / 8 * sm;
        if (l2r) {
            if (x + 1 < width) { const i = (y * width + (x + 1)) * 4; out[i] += e3; out[i + 1] += e3; out[i + 2] += e3; }
            if (y + 1 < height) {
                { const i = ((y + 1) * width + x) * 4; out[i] += e3; out[i + 1] += e3; out[i + 2] += e3; }
                if (x + 1 < width) { const i = ((y + 1) * width + (x + 1)) * 4; out[i] += e2; out[i + 1] += e2; out[i + 2] += e2; }
            }
        } else {
            if (x - 1 >= 0) { const i = (y * width + (x - 1)) * 4; out[i] += e3; out[i + 1] += e3; out[i + 2] += e3; }
            if (y + 1 < height) {
                { const i = ((y + 1) * width + x) * 4; out[i] += e3; out[i + 1] += e3; out[i + 2] += e3; }
                if (x - 1 >= 0) { const i = ((y + 1) * width + (x - 1)) * 4; out[i] += e2; out[i + 1] += e2; out[i + 2] += e2; }
            }
        }
    }
    for (let y = 0; y < height; y++) {
        const l2r = !serp || y % 2 === 0;
        if (l2r) {
            for (let x = 0; x < width; x++) {
                const idx = (y * width + x) * 4;
                const oldV = out[idx]; const newV = oldV < thresh ? 0 : 255; const err = oldV - newV;
                out[idx] = out[idx + 1] = out[idx + 2] = newV; distribute(x, y, err, true);
            }
        } else {
            for (let x = width - 1; x >= 0; x--) {
                const idx = (y * width + x) * 4;
                const oldV = out[idx]; const newV = oldV < thresh ? 0 : 255; const err = oldV - newV;
                out[idx] = out[idx + 1] = out[idx + 2] = newV; distribute(x, y, err, false);
            }
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

// ---- Adaptive Ostromoukhov ----
function runAdaptiveOstromoukhov(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const thresh = params.threshold ?? 128;
    const sm = (params.strength ?? 100) / 100;
    const kernels = [{ w: [7, 3, 5, 1] }, { w: [5, 3, 3, 1] }, { w: [4, 3, 2, 1] }, { w: [3, 2, 2, 1] }, { w: [2, 2, 1, 1] }];
    for (let y = 0; y < height; y++) {
        const serp = params.serpentine && (y % 2 === 1);
        for (let xi = 0; xi < width; xi++) {
            const x = serp ? (width - 1 - xi) : xi;
            const idx = (y * width + x) * 4;
            const old = out[idx];
            const neu = old < thresh ? 0 : 255;
            const err = (old - neu) * sm;
            out[idx] = out[idx + 1] = out[idx + 2] = neu;
            const bucket = Math.min(4, Math.max(0, Math.floor(old / 51)));
            const weights = kernels[bucket].w;
            const denom = weights.reduce((a, b) => a + b, 0);
            const coords = serp ? [[x - 1, y], [x - 2, y], [x, y + 1], [x - 1, y + 1]] : [[x + 1, y], [x + 2, y], [x, y + 1], [x + 1, y + 1]];
            for (let c = 0; c < coords.length; c++) {
                const [nx, ny] = coords[c];
                if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
                    const nIdx = (ny * width + nx) * 4;
                    const dif = (err * weights[c]) / denom;
                    out[nIdx] += dif; out[nIdx + 1] += dif; out[nIdx + 2] += dif;
                }
            }
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

// ---- Adaptive FS (3x3 & 7x7) ----
function makeAdaptiveFS(radius) {
    return function (srcData, width, height, params) {
        const { threshold = 128, invert, serpentine } = params;
        const meanMap = computeMeanMap(srcData, width, height, radius);
        const work = new Float32Array(width * height);
        for (let i = 0, p = 0; i < srcData.length; i += 4, p++) work[p] = 0.299 * srcData[i] + 0.587 * srcData[i + 1] + 0.114 * srcData[i + 2];
        const bias = threshold - 128;
        for (let y = 0; y < height; y++) {
            const serp = serpentine && (y & 1) === 1;
            if (!serp) {
                for (let x = 0; x < width; x++) {
                    const idx = y * width + x; const localT = meanMap[idx] + bias;
                    const oldVal = work[idx]; const newVal = oldVal < localT ? 0 : 255; const err = oldVal - newVal; work[idx] = newVal;
                    if (x + 1 < width) work[idx + 1] += (err * 7) / 16;
                    if (y + 1 < height) { if (x > 0) work[idx + width - 1] += (err * 3) / 16; work[idx + width] += (err * 5) / 16; if (x + 1 < width) work[idx + width + 1] += (err * 1) / 16; }
                }
            } else {
                for (let x = width - 1; x >= 0; x--) {
                    const idx = y * width + x; const localT = meanMap[idx] + bias;
                    const oldVal = work[idx]; const newVal = oldVal < localT ? 0 : 255; const err = oldVal - newVal; work[idx] = newVal;
                    if (x - 1 >= 0) work[idx - 1] += (err * 7) / 16;
                    if (y + 1 < height) { if (x + 1 < width) work[idx + width + 1] += (err * 3) / 16; work[idx + width] += (err * 5) / 16; if (x - 1 >= 0) work[idx + width - 1] += (err * 1) / 16; }
                }
            }
        }
        const out = new Uint8ClampedArray(srcData.length);
        for (let p = 0, i = 0; p < work.length; p++, i += 4) {
            let v = work[p]; if (v < 0) v = 0; else if (v > 255) v = 255; if (invert) v = 255 - v;
            out[i] = out[i + 1] = out[i + 2] = v; out[i + 3] = 255;
        }
        return out;
    };
}

// ---- Riemersma ----
function runRiemersma(srcData, width, height, params) {
    const { threshold = 128, invert } = params;
    const weights = [1 / 16, 1 / 8, 1 / 4, 1 / 2];
    const bufferSize = 16;
    const out = new Uint8ClampedArray(srcData.length);
    const maxDim = Math.max(width, height);
    const order = Math.ceil(Math.log2(maxDim));
    const curve = hilbertCurve(order);
    const scaledCurve = scaleHilbertCurve(curve, width, height);
    const errorBuffer = new Float32Array(bufferSize);
    let bufferPos = 0;
    const totalWeight = weights.reduce((s, w) => s + w, 0);
    const normalizedWeights = weights.map(w => w / totalWeight);
    const lum = new Float32Array(width * height);
    for (let i = 0, p = 0; i < srcData.length; i += 4, p++) lum[p] = 0.299 * srcData[i] + 0.587 * srcData[i + 1] + 0.114 * srcData[i + 2];
    for (const [x, y] of scaledCurve) {
        if (x < 0 || x >= width || y < 0 || y >= height) continue;
        const lumIdx = y * width + x;
        let value = lum[lumIdx];
        for (let i = 0; i < Math.min(weights.length, bufferSize); i++) {
            const bufIdx = (bufferPos - i - 1 + bufferSize) % bufferSize;
            value += errorBuffer[bufIdx] * (normalizedWeights[i] || 0);
        }
        const newValue = value < threshold ? 0 : 255;
        const error = value - newValue;
        errorBuffer[bufferPos] = error;
        bufferPos = (bufferPos + 1) % bufferSize;
        const idx = (y * width + x) * 4;
        const finalValue = invert ? 255 - newValue : newValue;
        out[idx] = finalValue; out[idx + 1] = finalValue; out[idx + 2] = finalValue; out[idx + 3] = 255;
    }
    return out;
}

// ============================================================================
//  ORDERED / STOCHASTIC
// ============================================================================
function runBayerGeneric(srcData, width, height, params, size) {
    const out = new Uint8ClampedArray(srcData);
    const userT = params.threshold ?? 128;
    const thresholdOffset = userT - 128;
    const matrix = buildBayer(size);
    const denom = size * size;
    const scale255 = 255 / denom;
    const sizeMask = size - 1;
    for (let y = 0; y < height; y++) {
        const rowMod = y & sizeMask;
        const rowOffset = rowMod * size;
        for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            const g = out[i];
            const colMod = x & sizeMask;
            const matrixT = matrix[rowOffset + colMod] * scale255;
            const t = matrixT + thresholdOffset;
            const clampedT = t < 0 ? 0 : (t > 255 ? 255 : t);
            const v = g < clampedT ? 0 : 255;
            out[i] = v; out[i + 1] = v; out[i + 2] = v; out[i + 3] = 255;
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

function runBlueNoise(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const userT = params.threshold ?? 128;
    const thresholdOffset = userT - 128;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const g = out[idx];
            const cell = blueNoiseThreshold(x, y);
            const matrixT = cell * 255;
            let t = matrixT + thresholdOffset;
            if (t < 0) t = 0; else if (t > 255) t = 255;
            const v = g < t ? 0 : 255;
            out[idx] = out[idx + 1] = out[idx + 2] = v; out[idx + 3] = 255;
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

// ============================================================================
//  OTHER / EXPERIMENTAL
// ============================================================================
function runBinaryThreshold(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const thresh = params.threshold ?? 128;
    for (let i = 0; i < out.length; i += 4) {
        const v = out[i] < thresh ? 0 : 255;
        out[i] = out[i + 1] = out[i + 2] = v; out[i + 3] = 255;
    }
    if (params.invert) applyInvert(out);
    return out;
}

function runRandomThreshold(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const userT = params.threshold ?? 128;
    const pivot = userT / 255;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            let g = out[i]; if (params.invert) g = 255 - g;
            const normalized = g / 255;
            const r = Math.random();
            const mix = (normalized + r) / 2;
            const bw = mix < pivot ? 0 : 255;
            out[i] = out[i + 1] = out[i + 2] = bw; out[i + 3] = 255;
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

function runDotDiffusionSimple(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const t = params.threshold ?? 128;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            let g = out[i]; if (params.invert) g = 255 - g;
            const mask = ((x & 1) ^ (y & 1)) ? t : 255 - t;
            const bw = g < mask ? 0 : 255;
            out[i] = out[i + 1] = out[i + 2] = bw; out[i + 3] = 255;
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

function runHalftone(srcData, width, height, params) {
    const out = new Uint8ClampedArray(srcData);
    const dotSize = 6;
    const thresh = params.threshold ?? 128;
    for (let y = 0; y < height; y += dotSize) {
        for (let x = 0; x < width; x += dotSize) {
            let sum = 0, count = 0;
            for (let dy = 0; dy < dotSize; dy++) {
                for (let dx = 0; dx < dotSize; dx++) {
                    const nx = x + dx, ny = y + dy;
                    if (nx < width && ny < height) { const i = (ny * width + nx) * 4; sum += out[i]; count++; }
                }
            }
            const avg = sum / count;
            const isDot = avg < thresh;
            for (let dy = 0; dy < dotSize; dy++) {
                for (let dx = 0; dx < dotSize; dx++) {
                    const nx = x + dx, ny = y + dy;
                    if (nx < width && ny < height) {
                        const i = (ny * width + nx) * 4;
                        const cx = dotSize / 2 - 0.5, cy = dotSize / 2 - 0.5;
                        const dist = Math.sqrt((dx - cx) ** 2 + (dy - cy) ** 2);
                        const radius = isDot ? dotSize / 2.2 : dotSize / 3.5;
                        const v = dist < radius ? 0 : 255;
                        out[i] = out[i + 1] = out[i + 2] = v; out[i + 3] = 255;
                    }
                }
            }
        }
    }
    if (params.invert) applyInvert(out);
    return out;
}

function runPosterize(srcData, width, height, params) {
    const { threshold = 128, invert } = params;
    const out = new Uint8ClampedArray(srcData.length);
    const levels = Math.max(2, Math.min(16, Math.floor(threshold / 16) + 2));
    const stepSize = 256 / levels;
    for (let i = 0; i < srcData.length; i += 4) {
        let gray = 0.299 * srcData[i] + 0.587 * srcData[i + 1] + 0.114 * srcData[i + 2];
        if (invert) gray = 255 - gray;
        const level = Math.floor(gray / stepSize);
        const posterized = Math.min(255, level * stepSize);
        out[i] = posterized; out[i + 1] = posterized; out[i + 2] = posterized; out[i + 3] = 255;
    }
    return out;
}

function runWoodcut(srcData, width, height, params) {
    const { threshold = 128, invert } = params;
    const out = new Uint8ClampedArray(srcData.length);
    const edges = new Float32Array(width * height);
    const gray = new Float32Array(width * height);
    for (let i = 0, p = 0; i < srcData.length; i += 4, p++) gray[p] = 0.299 * srcData[i] + 0.587 * srcData[i + 1] + 0.114 * srcData[i + 2];
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;
            const gx = -1 * gray[(y - 1) * width + (x - 1)] + 1 * gray[(y - 1) * width + (x + 1)] +
                -2 * gray[y * width + (x - 1)] + 2 * gray[y * width + (x + 1)] +
                -1 * gray[(y + 1) * width + (x - 1)] + 1 * gray[(y + 1) * width + (x + 1)];
            const gy = -1 * gray[(y - 1) * width + (x - 1)] - 2 * gray[(y - 1) * width + x] - 1 * gray[(y - 1) * width + (x + 1)] +
                1 * gray[(y + 1) * width + (x - 1)] + 2 * gray[(y + 1) * width + x] + 1 * gray[(y + 1) * width + (x + 1)];
            edges[idx] = Math.sqrt(gx * gx + gy * gy);
        }
    }
    let maxEdge = 0;
    for (let i = 0; i < edges.length; i++) if (edges[i] > maxEdge) maxEdge = edges[i];
    if (maxEdge > 0) for (let i = 0; i < edges.length; i++) edges[i] = (edges[i] / maxEdge) * 255;
    const edgeStrength = (threshold / 255) * 0.7;
    for (let i = 0, p = 0; i < srcData.length; i += 4, p++) {
        let value = gray[p];
        value = value - edges[p] * edgeStrength;
        value = Math.max(0, Math.min(255, value));
        if (invert) value = 255 - value;
        const result = value > threshold ? 255 : 0;
        out[i] = result; out[i + 1] = result; out[i + 2] = result; out[i + 3] = 255;
    }
    return out;
}

function runStipple(srcData, width, height, params) {
    const { threshold = 128, invert } = params;
    const out = new Uint8ClampedArray(srcData.length);
    for (let i = 0; i < out.length; i += 4) { out[i] = 255; out[i + 1] = 255; out[i + 2] = 255; out[i + 3] = 255; }
    const density = 1.0 - (threshold / 255) * 0.8;
    const gray = new Float32Array(width * height);
    for (let i = 0, p = 0; i < srcData.length; i += 4, p++) gray[p] = 0.299 * srcData[i] + 0.587 * srcData[i + 1] + 0.114 * srcData[i + 2];
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const idx = y * width + x;
            let brightness = gray[idx];
            if (invert) brightness = 255 - brightness;
            const darkness = 1.0 - (brightness / 255);
            const probability = darkness * density;
            const hashX = (x * 73856093) ^ (y * 19349663);
            const pseudoRandom = (hashX & 0xFFFFFF) / 0xFFFFFF;
            if (pseudoRandom < probability) {
                const i = (y * width + x) * 4;
                out[i] = 0; out[i + 1] = 0; out[i + 2] = 0; out[i + 3] = 255;
            }
        }
    }
    return out;
}

// ---- ASCII Mosaic (uses canvas text rendering) ----
const ASCII_RAMP = '@%#*+=-:. ';
const ASCII_CELL_SIZE = 10;
function runAsciiMosaic(srcData, width, height, params) {
    const thresh = params.threshold ?? 128;
    const bias = ((thresh - 128) / 255) * 64;
    const ramp = ASCII_RAMP;
    const rampLen = ramp.length;
    const cols = Math.ceil(width / ASCII_CELL_SIZE);
    const rows = Math.ceil(height / ASCII_CELL_SIZE);
    const off = createCanvas(width, height);
    const g = off.getContext('2d');
    if (!g) return srcData;
    g.fillStyle = params.invert ? '#fff' : '#000';
    g.fillRect(0, 0, width, height);
    g.font = `${ASCII_CELL_SIZE}px monospace`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = params.invert ? '#000' : '#fff';
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const bx = c * ASCII_CELL_SIZE, by = r * ASCII_CELL_SIZE;
            let sum = 0, count = 0;
            for (let y = 0; y < ASCII_CELL_SIZE; y++) {
                const ny = by + y; if (ny >= height) break;
                for (let x = 0; x < ASCII_CELL_SIZE; x++) {
                    const nx = bx + x; if (nx >= width) break;
                    const i = (ny * width + nx) * 4;
                    sum += srcData[i]; count++;
                }
            }
            if (!count) continue;
            let avg = sum / count;
            if (params.invert) avg = 255 - avg;
            avg = Math.max(0, Math.min(255, avg + bias));
            const idx = Math.min(rampLen - 1, Math.max(0, Math.round(((255 - avg) / 255) * (rampLen - 1))));
            g.fillText(ramp[idx], bx + ASCII_CELL_SIZE / 2, by + ASCII_CELL_SIZE / 2 + 0.5);
        }
    }
    const data = g.getImageData(0, 0, width, height).data;
    return new Uint8ClampedArray(data);
}

// ============================================================================
//  EXPORTS — all wrapped as (canvas, ctx, ...args) => Promise
//  Error diffusion FX args: (threshold, serpentine, invert, strength)
//  Ordered/other FX args:   (threshold, invert)
// ============================================================================
// ============================================================================
//  FUEGOCODING DITHER (ported from fuegocoding/dither)
//  Grid-based dithering with rotated-sine halftone, hash noise, and crosshatch
//  patterns. Args: (gridSize, threshold, invert)
//  threshold is 0..1 (normalized), gridSize is pixel block size
// ============================================================================
function runFcHalftone(srcData, width, height, params) {
    const gridSize = Math.max(1, Math.floor(params.gridSize || 2));
    const threshold = params.threshold ?? 0.5;
    const invert = !!params.invert;
    const out = new Uint8ClampedArray(srcData.length);
    const angle = Math.PI / 4;
    const scale = gridSize * 2;
    for (let y = 0; y < height; y += gridSize) {
        for (let x = 0; x < width; x += gridSize) {
            const srcIdx = (y * width + x) * 4;
            const r = srcData[srcIdx] || 0;
            const g = srcData[srcIdx + 1] || 0;
            const b = srcData[srcIdx + 2] || 0;
            const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const rotX = x * Math.cos(angle) + y * Math.sin(angle);
            const rotY = -x * Math.sin(angle) + y * Math.cos(angle);
            const pattern = (Math.sin(rotX / scale) + Math.sin(rotY / scale) + 2) / 4;
            let ditherT = pattern * (1 - threshold) + threshold * 0.5;
            const isDark = lum < ditherT;
            const v = (invert ? !isDark : isDark) ? 0 : 255;
            for (let dy = 0; dy < gridSize && y + dy < height; dy++) {
                for (let dx = 0; dx < gridSize && x + dx < width; dx++) {
                    const i = ((y + dy) * width + (x + dx)) * 4;
                    out[i] = v; out[i + 1] = v; out[i + 2] = v; out[i + 3] = 255;
                }
            }
        }
    }
    return out;
}

function runFcNoise(srcData, width, height, params) {
    const gridSize = Math.max(1, Math.floor(params.gridSize || 2));
    const threshold = params.threshold ?? 0.5;
    const invert = !!params.invert;
    const out = new Uint8ClampedArray(srcData.length);
    for (let y = 0; y < height; y += gridSize) {
        for (let x = 0; x < width; x += gridSize) {
            const srcIdx = (y * width + x) * 4;
            const r = srcData[srcIdx] || 0;
            const g = srcData[srcIdx + 1] || 0;
            const b = srcData[srcIdx + 2] || 0;
            const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const noiseVal = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
            let ditherT = (noiseVal - Math.floor(noiseVal)) * (1 - threshold) + threshold * 0.5;
            const isDark = lum < ditherT;
            const v = (invert ? !isDark : isDark) ? 0 : 255;
            for (let dy = 0; dy < gridSize && y + dy < height; dy++) {
                for (let dx = 0; dx < gridSize && x + dx < width; dx++) {
                    const i = ((y + dy) * width + (x + dx)) * 4;
                    out[i] = v; out[i + 1] = v; out[i + 2] = v; out[i + 3] = 255;
                }
            }
        }
    }
    return out;
}

function runFcCrosshatch(srcData, width, height, params) {
    const gridSize = Math.max(1, Math.floor(params.gridSize || 2));
    const threshold = params.threshold ?? 0.5;
    const invert = !!params.invert;
    const out = new Uint8ClampedArray(srcData.length);
    for (let y = 0; y < height; y += gridSize) {
        for (let x = 0; x < width; x += gridSize) {
            const srcIdx = (y * width + x) * 4;
            const r = srcData[srcIdx] || 0;
            const g = srcData[srcIdx + 1] || 0;
            const b = srcData[srcIdx + 2] || 0;
            const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const line1 = Math.abs((x + y) % (gridSize * 2)) < gridSize ? 1 : 0;
            const line2 = Math.abs((x - y + gridSize * 4) % (gridSize * 2)) < gridSize ? 1 : 0;
            let ditherT = ((line1 + line2) / 2) * (1 - threshold) + threshold * 0.5;
            const isDark = lum < ditherT;
            const v = (invert ? !isDark : isDark) ? 0 : 255;
            for (let dy = 0; dy < gridSize && y + dy < height; dy++) {
                for (let dx = 0; dx < gridSize && x + dx < width; dx++) {
                    const i = ((y + dy) * width + (x + dx)) * 4;
                    out[i] = v; out[i + 1] = v; out[i + 2] = v; out[i + 3] = 255;
                }
            }
        }
    }
    return out;
}

module.exports = {
    // Error diffusion
    ditherFloydSteinberg: makeFxRunner(runFloydSteinberg),
    ditherFalseFloydSteinberg: makeFxRunner(runFalseFloydSteinberg),
    ditherAtkinson: makeFxRunner(runAtkinson),
    ditherSierraLite: makeFxRunner(createKernelRunner([[0, 0, 2], [1, 1, 0]], 4)),
    ditherBurkes: makeFxRunner(createKernelRunner([[0, 0, 0, 8, 4], [2, 4, 8, 4, 2]], 32)),
    ditherStucki: makeFxRunner(createKernelRunner([[0, 0, 0, 8, 4], [2, 4, 8, 4, 2], [1, 2, 4, 2, 1]], 42)),
    ditherSierra: makeFxRunner(createKernelRunner([[0, 0, 0, 5, 3], [2, 4, 5, 4, 2], [0, 2, 3, 2, 0]], 32)),
    ditherJJN: makeFxRunner(createKernelRunner([[0, 0, 0, 7, 5], [3, 5, 7, 5, 3], [1, 3, 5, 3, 1]], 48)),
    ditherTwoRowSierra: makeFxRunner(createKernelRunner([[0, 0, 0, 4, 3], [1, 2, 3, 2, 1]], 16)),
    ditherStevensonArce: makeFxRunner(createKernelRunner([
        [0, 0, 0, 0, 0, 32, 0, 0, 0, 0, 0],
        [12, 0, 26, 0, 30, 0, 30, 0, 26, 0, 12],
        [0, 12, 0, 26, 0, 12, 0, 26, 0, 12, 0]
    ], 200)),
    ditherSierra24A: makeFxRunner(createKernelRunner([[0, 0, 4, 5, 4], [2, 4, 5, 4, 2], [0, 2, 3, 2, 0]], 37)),
    ditherOstromoukhov: makeFxRunner(runAdaptiveOstromoukhov),
    ditherAdaptiveFS3: makeFxRunner(makeAdaptiveFS(1)),
    ditherAdaptiveFS7: makeFxRunner(makeAdaptiveFS(3)),
    ditherRiemersma: makeFxRunner(runRiemersma),

    // Ordered / stochastic
    ditherBayer2: makeFxRunner((s, w, h, p) => runBayerGeneric(s, w, h, p, 2)),
    ditherBayer4: makeFxRunner((s, w, h, p) => runBayerGeneric(s, w, h, p, 4)),
    ditherBayer8: makeFxRunner((s, w, h, p) => runBayerGeneric(s, w, h, p, 8)),
    ditherBayer16: makeFxRunner((s, w, h, p) => runBayerGeneric(s, w, h, p, 16)),
    ditherBayer32: makeFxRunner((s, w, h, p) => runBayerGeneric(s, w, h, p, 32)),
    ditherBlueNoise: makeFxRunner(runBlueNoise),

    // Other / experimental
    ditherBinaryThreshold: makeFxRunner(runBinaryThreshold),
    ditherHalftone: makeFxRunner(runHalftone),
    ditherRandomThreshold: makeFxRunner(runRandomThreshold),
    ditherDotDiffusion: makeFxRunner(runDotDiffusionSimple),
    ditherAsciiMosaic: makeFxRunner(runAsciiMosaic),
    ditherPosterize: makeFxRunner(runPosterize),
    ditherWoodcut: makeFxRunner(runWoodcut),
    ditherStipple: makeFxRunner(runStipple),

    // Fuegocoding dither (grid-based patterns)
    ditherFcHalftone: makeFxRunner(runFcHalftone),
    ditherFcNoise: makeFxRunner(runFcNoise),
    ditherFcCrosshatch: makeFxRunner(runFcCrosshatch),
};
